import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, Users, Send, Trash2, Link2, Clock, FileText, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { Title, Kpi, Badge, Empty } from './ui';
import Texto from '../../utils/markdown';

/* El archivo de todas las reuniones. Las que son con un cliente se pueden
   publicar en su portal; las que son con un prospecto quedan acá hasta que
   se le asigne un cliente, sin perder lo que se habló. */

const AR = -3 * 3600 * 1000;
const hoyISO = () => new Date(Date.now() + AR).toISOString().slice(0, 10);
const menos = (dias) => new Date(Date.now() + AR - dias * 86400000).toISOString().slice(0, 10);
const primeroDelMes = () => hoyISO().slice(0, 8) + '01';
const primeroDelAnio = () => hoyISO().slice(0, 4) + '-01-01';

const RANGOS = [
  { key: 'semana', label: 'Esta semana', desde: () => menos(7) },
  { key: 'mes', label: 'Este mes', desde: primeroDelMes },
  { key: 'anio', label: 'Este año', desde: primeroDelAnio },
  { key: 'todo', label: 'Todas', desde: () => '' },
  { key: 'custom', label: 'Personalizado', desde: () => '' },
];

const cuando = (f) => {
  if (!f) return '—';
  const d = new Date(f);
  const dia = String(f).slice(0, 10).split('-').reverse().join('/');
  const hora = String(f).slice(11, 16);
  return hora && hora !== '00:00' ? `${dia} · ${hora}` : dia;
};

export default function Reuniones() {
  const [data, setData] = useState(null);
  const [rango, setRango] = useState('mes');
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState('');
  const [soloSinCliente, setSoloSinCliente] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [abierta, setAbierta] = useState(null);
  const [nueva, setNueva] = useState(null);
  const [clientes, setClientes] = useState([]);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (desde) q.set('desde', desde);
    if (hasta) q.set('hasta', hasta);
    if (soloSinCliente) q.set('sin_cliente', '1');
    if (buscar) q.set('buscar', buscar);
    return api.get('/agency/reuniones?' + q.toString())
      .then(r => setData(r.data))
      .catch(() => toast.error('No se pudieron cargar'));
  }, [desde, hasta, soloSinCliente, buscar]);
  useEffect(() => { const t = setTimeout(load, buscar ? 300 : 0); return () => clearTimeout(t); }, [load, buscar]);
  useEffect(() => { api.get('/agency/clientes?estado=activo').then(r => setClientes(r.data || [])).catch(() => {}); }, []);

  const elegirRango = (r) => {
    setRango(r.key);
    if (r.key === 'custom') return;
    setDesde(r.desde()); setHasta('');
  };

  const abrir = async (r) => {
    try { const { data: d } = await api.get('/agency/reuniones/' + r.id); setAbierta(d); }
    catch { toast.error('No se pudo abrir'); }
  };

  const asignar = async (r, clienteId) => {
    try {
      await api.put('/agency/reuniones/' + r.id, { cliente_id: clienteId || null });
      toast.success(clienteId ? 'Asignada' : 'Sin cliente');
      setAbierta(a => (a && a.id === r.id ? { ...a, cliente_id: clienteId || null } : a));
      load();
    } catch { toast.error('No se pudo asignar'); }
  };

  const publicar = async (r) => {
    if (!r.cliente_id) return toast.error('Primero asignale un cliente');
    const avisar = window.confirm(
      `Se publica en el portal de ${r.cliente || 'el cliente'} y la va a poder leer.\n\n¿Le avisamos por mail?\n\nAceptar: se publica y le avisamos.\nCancelar: se publica sin avisarle.`);
    try {
      const { data: res } = await api.post(`/agency/reuniones/${r.id}/publicar`, { notificar: avisar ? 'ambos' : 'portal' });
      toast.success(res.aviso?.enviado ? 'Publicada y avisada' : 'Publicada en su portal');
      setAbierta(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo'); }
  };

  const borrar = async (r) => {
    if (!window.confirm(`¿Borrar "${r.titulo}"?\n\nSe pierde la minuta y la transcripción.`)) return;
    try { await api.delete('/agency/reuniones/' + r.id); setAbierta(null); load(); }
    catch { toast.error('No se pudo borrar'); }
  };

  const crear = async (e) => {
    e.preventDefault();
    try {
      await api.post('/agency/reuniones', { ...nueva, fecha: nueva.fecha || new Date().toISOString().slice(0, 19).replace('T', ' ') });
      toast.success('Reunión cargada'); setNueva(null); load();
    } catch { toast.error('No se pudo crear'); }
  };

  const r = data?.resumen || {};
  const filas = data?.reuniones || [];

  return (
    <div>
      <Title right={
        <button className="btn btn-primary btn-sm" onClick={() => setNueva({ titulo: '', con_quien: '', cliente_id: '', minuta: '', fecha: '' })}>
          <Plus size={15} /> Cargar reunión
        </button>
      }>Reuniones</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Kpi label="Este mes" value={r.mes ?? '—'} icono={Clock} />
        <Kpi label="Últimos 7 días" value={r.semana ?? '—'} icono={FileText} />
        <Kpi label="Sin cliente" value={r.sin_cliente ?? '—'} icono={Link2}
          color={r.sin_cliente ? '#d2930c' : undefined}
          sub={r.sin_cliente ? 'Se pueden asignar' : 'Todas asignadas'}
          onClick={() => { setSoloSinCliente(true); setRango('todo'); setDesde(''); setHasta(''); }} />
        <Kpi label="Todas" value={r.total ?? '—'} icono={Users} />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', gap: 10, padding: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="solapas">
            {RANGOS.map(x => (
              <button key={x.key} onClick={() => elegirRango(x)} className={rango === x.key ? 'on' : ''}>{x.label}</button>
            ))}
          </div>
          {rango === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ width: 'auto' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>a</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={{ width: 'auto' }} />
            </div>
          )}
          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar por título, cliente o contenido" style={{ paddingLeft: 34 }} />
          </div>
          <label className="tareas-ver-hechas">
            <input type="checkbox" checked={soloSinCliente} onChange={e => setSoloSinCliente(e.target.checked)} /> Solo sin cliente
          </label>
        </div>

        {!data ? <div style={{ padding: 20, color: 'var(--text-muted)' }}>Cargando...</div>
          : filas.length === 0 ? <div style={{ padding: 20 }}><Empty>No hay reuniones en ese período</Empty></div>
            : (
              <div style={{ borderTop: '1px solid var(--border)' }}>
                {filas.map(x => (
                  <div key={x.id} className="reunion-fila" onClick={() => abrir(x)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{x.titulo}</span>
                        {x.cliente
                          ? <Badge label={x.cliente} color="var(--accent)" bg="var(--accent-glow)" />
                          : <Badge label="Sin cliente" color="#d2930c" bg="rgba(210,147,12,0.15)" />}
                        {x.visible_cliente === 1 && x.cliente_id && <Badge label="En su portal" color="#12a37a" bg="rgba(18,163,122,0.15)" />}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <span>{cuando(x.fecha)}</span>
                        {x.con_quien && <span>con {x.con_quien}</span>}
                        {x.duracion_min ? <span>{x.duracion_min} min</span> : null}
                        {x.tareas?.length ? <span>{x.tareas.length} tarea{x.tareas.length === 1 ? '' : 's'}</span> : null}
                        {x.largo_transcripcion > 0 && <span>con transcripción</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </div>

      {/* Ficha de una reunión */}
      {abierta && (
        <div className="modal-overlay" onClick={() => setAbierta(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <h2 style={{ marginBottom: 4 }}>{abierta.titulo}</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>
              {cuando(abierta.fecha)}{abierta.con_quien ? ` · con ${abierta.con_quien}` : ''}
              {abierta.duracion_min ? ` · ${abierta.duracion_min} min` : ''}
            </div>

            <div className="form-group">
              <label>Cliente</label>
              <select value={abierta.cliente_id || ''} onChange={e => asignar(abierta, e.target.value)}>
                <option value="">Sin cliente todavía</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
              {!abierta.cliente_id && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                  Se la podés asignar cuando quieras, también más adelante si se vuelve cliente.
                </div>
              )}
            </div>

            {abierta.tareas?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                  Qué quedó pendiente
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {abierta.tareas.map((t, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
                      <Check size={14} style={{ marginTop: 2, color: 'var(--accent)', flexShrink: 0 }} />
                      <span>{typeof t === 'string' ? t : t.texto}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ background: 'var(--bg-hover)', borderRadius: 12, padding: '4px 16px 8px', fontSize: 13.5,
              color: 'var(--text-secondary)', maxHeight: 320, overflowY: 'auto' }}>
              {abierta.minuta ? <Texto texto={abierta.minuta} /> : 'Sin minuta cargada'}
            </div>

            {abierta.transcripcion && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                  Ver la transcripción completa
                </summary>
                <div style={{ marginTop: 10, background: 'var(--bg-hover)', borderRadius: 10, padding: 14,
                  fontSize: 12.5, whiteSpace: 'pre-line', lineHeight: 1.7, color: 'var(--text-muted)', maxHeight: 300, overflowY: 'auto' }}>
                  {abierta.transcripcion}
                </div>
              </details>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => borrar(abierta)} style={{ marginRight: 'auto', color: 'var(--danger)' }}>
                <Trash2 size={15} /> Borrar
              </button>
              <button className="btn btn-secondary" onClick={() => setAbierta(null)}>Cerrar</button>
              {abierta.cliente_id && (
                <button className="btn btn-primary" onClick={() => publicar(abierta)}>
                  <Send size={15} /> {abierta.visible_cliente ? 'Volver a avisar' : 'Publicar en su portal'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cargar una a mano */}
      {nueva && (
        <div className="modal-overlay" onClick={() => setNueva(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h2>Cargar una reunión</h2>
            <form onSubmit={crear}>
              <div className="form-group"><label>Título</label>
                <input value={nueva.titulo} autoFocus required placeholder="Reunión de seguimiento"
                  onChange={e => setNueva({ ...nueva, titulo: e.target.value })} /></div>
              <div className="grilla-datos" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div className="form-group"><label>Cuándo fue</label>
                  <input type="datetime-local" value={nueva.fecha}
                    onChange={e => setNueva({ ...nueva, fecha: e.target.value.replace('T', ' ') })} /></div>
                <div className="form-group"><label>Con quién</label>
                  <input value={nueva.con_quien} placeholder="Nombres de los participantes"
                    onChange={e => setNueva({ ...nueva, con_quien: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Cliente <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· se puede asignar después</span></label>
                <select value={nueva.cliente_id} onChange={e => setNueva({ ...nueva, cliente_id: e.target.value })}>
                  <option value="">Sin cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select></div>
              <div className="form-group"><label>Minuta</label>
                <textarea rows={7} value={nueva.minuta} placeholder="El resumen de lo que se habló"
                  onChange={e => setNueva({ ...nueva, minuta: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setNueva(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
